import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Check, Lock } from "lucide-react";
import axios from "axios";
import { useCart } from "../context/CartContext";
import { toast } from "sonner";
import { API } from "../lib/api";


const TAX_RATE = 0.065; // 6.5% Florida
const SHIPPING_FLAT = 6.0;
const PROMO_CODES = { SWEET10: 0.1, FRESH15: 0.15 };

const fmt = (n) => `$${n.toFixed(2)}`;

export default function Checkout() {
  const navigate = useNavigate();
  const { items, subtotal, clear } = useCart();

  const [step, setStep] = useState("details"); // details | payment
  const [submitting, setSubmitting] = useState(false);
  const [promo, setPromo] = useState("");
  const [appliedPromo, setAppliedPromo] = useState(null);

  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    address: "",
    city: "",
    state: "FL",
    zip: "",
    deliveryNotes: "",
    cardName: "",
    cardNumber: "",
    cardExp: "",
    cardCvc: "",
  });

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const applyPromo = (e) => {
    e.preventDefault();
    const code = promo.trim().toUpperCase();
    if (PROMO_CODES[code]) {
      setAppliedPromo({ code, discount: PROMO_CODES[code] });
      toast.success(`Promo ${code} applied`, { description: `${PROMO_CODES[code] * 100}% off your subtotal.` });
    } else {
      toast.error("Invalid promo code", { description: "Try SWEET10 or FRESH15." });
    }
  };

  const discount = appliedPromo ? subtotal * appliedPromo.discount : 0;
  const taxed = subtotal - discount;
  const tax = taxed * TAX_RATE;
  const shipping = subtotal > 0 ? SHIPPING_FLAT : 0;
  const total = taxed + tax + shipping;

  const submitOrder = async (e) => {
    e.preventDefault();
    if (items.length === 0) {
      toast.error("Your cart is empty.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/orders`, {
        customer: {
          first_name: form.firstName,
          last_name: form.lastName,
          email: form.email,
          phone: form.phone,
        },
        shipping_address: {
          line1: form.address,
          city: form.city,
          state: form.state,
          zip: form.zip,
        },
        delivery_notes: form.deliveryNotes,
        items: items.map((i) => ({
          product_id: i.id,
          name: i.name,
          price: i.price,
          quantity: i.qty,
          image: i.image,
        })),
        subtotal,
        discount,
        tax,
        shipping,
        total,
        promo_code: appliedPromo?.code || null,
      });
      clear();
      navigate(`/confirmation/${data.order_number}`, { state: { order: data } });
    } catch (err) {
      toast.error("Couldn't place order", { description: "Please try again in a moment." });
    } finally {
      setSubmitting(false);
    }
  };

  if (items.length === 0) {
    return (
      <div className="min-h-screen bg-shay-cream pt-32 pb-24 px-6" data-testid="checkout-empty">
        <div className="max-w-md mx-auto text-center">
          <p className="eyebrow">Your Cart Is Empty</p>
          <h1 className="font-display text-4xl md:text-5xl text-shay-navy mt-4 tracking-tight">
            Nothing to checkout — yet.
          </h1>
          <p className="mt-5 text-shay-chocolate/70">
            Pick something delicious first. Your future self will thank you.
          </p>
          <button onClick={() => navigate("/menu")} className="btn-gold mt-8" data-testid="empty-go-menu-btn">
            Browse Menu
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-shay-cream min-h-screen pt-24 md:pt-28 pb-24" data-testid="checkout-page">
      <div className="max-w-7xl mx-auto px-6 md:px-8">
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-2 text-sm text-shay-navy/70 hover:text-shay-gold mb-6"
          data-testid="checkout-back-btn"
        >
          <ArrowLeft size={16} /> Continue Shopping
        </button>

        <div className="grid lg:grid-cols-12 gap-12">
          {/* Left: form */}
          <form onSubmit={submitOrder} className="lg:col-span-7 space-y-10" data-testid="checkout-form">
            <header>
              <p className="eyebrow">Checkout</p>
              <h1 className="font-display text-4xl md:text-5xl text-shay-navy mt-3 tracking-tight">
                Almost <span className="italic text-shay-gold">there.</span>
              </h1>
            </header>

            {/* Contact */}
            <section>
              <h2 className="font-display text-2xl text-shay-navy mb-4">Contact</h2>
              <div className="grid sm:grid-cols-2 gap-x-6 gap-y-4">
                <input required name="firstName" value={form.firstName} onChange={handleChange} placeholder="First name" className="field-input" data-testid="checkout-first-name" />
                <input required name="lastName" value={form.lastName} onChange={handleChange} placeholder="Last name" className="field-input" data-testid="checkout-last-name" />
                <input required type="email" name="email" value={form.email} onChange={handleChange} placeholder="Email address" className="field-input sm:col-span-2" data-testid="checkout-email" />
                <input required name="phone" value={form.phone} onChange={handleChange} placeholder="Phone number" className="field-input sm:col-span-2" data-testid="checkout-phone" />
              </div>
            </section>

            {/* Delivery */}
            <section>
              <h2 className="font-display text-2xl text-shay-navy mb-4">Delivery Address</h2>
              <div className="grid sm:grid-cols-6 gap-x-6 gap-y-4">
                <input required name="address" value={form.address} onChange={handleChange} placeholder="Street address" className="field-input sm:col-span-6" data-testid="checkout-address" />
                <input required name="city" value={form.city} onChange={handleChange} placeholder="City" className="field-input sm:col-span-3" data-testid="checkout-city" />
                <input required name="state" value={form.state} onChange={handleChange} placeholder="State" className="field-input sm:col-span-1" data-testid="checkout-state" />
                <input required name="zip" value={form.zip} onChange={handleChange} placeholder="ZIP" className="field-input sm:col-span-2" data-testid="checkout-zip" />
                <textarea name="deliveryNotes" value={form.deliveryNotes} onChange={handleChange} placeholder="Delivery notes (gate code, leave at door, etc.)" rows={2} className="field-input sm:col-span-6 resize-none" data-testid="checkout-notes" />
              </div>
            </section>

            {/* Payment */}
            <section>
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-display text-2xl text-shay-navy">Payment</h2>
                <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-shay-chocolate/60">
                  <Lock size={11} /> Demo Checkout — Not a real charge
                </span>
              </div>
              <div className="grid sm:grid-cols-6 gap-x-6 gap-y-4">
                <input required name="cardName" value={form.cardName} onChange={handleChange} placeholder="Name on card" className="field-input sm:col-span-6" data-testid="checkout-card-name" />
                <input required name="cardNumber" value={form.cardNumber} onChange={handleChange} placeholder="1234 5678 9012 3456" inputMode="numeric" className="field-input sm:col-span-6" data-testid="checkout-card-number" />
                <input required name="cardExp" value={form.cardExp} onChange={handleChange} placeholder="MM / YY" className="field-input sm:col-span-3" data-testid="checkout-card-exp" />
                <input required name="cardCvc" value={form.cardCvc} onChange={handleChange} placeholder="CVC" className="field-input sm:col-span-3" data-testid="checkout-card-cvc" />
              </div>
            </section>

            <div className="pt-4">
              <button
                type="submit"
                disabled={submitting}
                className="btn-gold w-full text-base disabled:opacity-60 disabled:cursor-not-allowed"
                data-testid="checkout-place-order-btn"
              >
                {submitting ? "Placing Order..." : `Place Order · ${fmt(total)}`}
              </button>
              <p className="mt-6 text-[10px] uppercase tracking-widest text-shay-chocolate/60 leading-relaxed">
                <span className="text-shay-gold">Allergen Notice —</span> Baked in a home kitchen with wheat, dairy,
                eggs, soy, peanuts, and tree nuts. We cannot guarantee any item is completely free of allergens.
              </p>
            </div>
          </form>

          {/* Right: order summary */}
          <aside className="lg:col-span-5 lg:sticky lg:top-28 self-start">
            <div className="bg-shay-navy text-shay-cream p-7 md:p-9" data-testid="order-summary">
              <h2 className="font-display text-2xl mb-6">Order Summary</h2>
              <ul className="space-y-4 max-h-72 overflow-y-auto pr-1">
                {items.map((i) => (
                  <li key={i.id} className="flex items-center gap-4">
                    <div className="h-14 w-14 shrink-0 bg-shay-cream/10 overflow-hidden">
                      <img src={i.image} alt={i.name} className="w-full h-full object-cover" loading="lazy" decoding="async" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-display text-base leading-tight truncate">{i.name}</p>
                      <p className="text-xs text-shay-cream/60">Qty {i.qty}</p>
                    </div>
                    <p className="text-sm">{fmt(i.price * i.qty)}</p>
                  </li>
                ))}
              </ul>

              <form onSubmit={applyPromo} className="mt-7 pt-6 border-t border-shay-gold/20 flex gap-3">
                <input
                  value={promo}
                  onChange={(e) => setPromo(e.target.value)}
                  placeholder="Promo code"
                  className="flex-1 bg-transparent border-b border-shay-cream/30 px-0 py-2 text-shay-cream placeholder:text-shay-cream/40 focus:outline-none focus:border-shay-gold uppercase tracking-widest text-sm"
                  data-testid="promo-input"
                />
                <button type="submit" className="text-xs uppercase tracking-widest text-shay-gold hover:underline" data-testid="apply-promo-btn">
                  Apply
                </button>
              </form>
              {appliedPromo && (
                <p className="mt-2 text-xs text-shay-gold flex items-center gap-1.5" data-testid="applied-promo-label">
                  <Check size={12} /> {appliedPromo.code} — {appliedPromo.discount * 100}% off applied
                </p>
              )}

              <dl className="mt-7 space-y-3 text-sm">
                <div className="flex justify-between"><dt className="text-shay-cream/70">Subtotal</dt><dd>{fmt(subtotal)}</dd></div>
                {discount > 0 && (
                  <div className="flex justify-between text-shay-gold"><dt>Discount</dt><dd>-{fmt(discount)}</dd></div>
                )}
                <div className="flex justify-between"><dt className="text-shay-cream/70">Sales Tax (6.5%)</dt><dd>{fmt(tax)}</dd></div>
                <div className="flex justify-between"><dt className="text-shay-cream/70">Local Delivery</dt><dd>{fmt(shipping)}</dd></div>
                <div className="flex justify-between pt-4 border-t border-shay-gold/20 font-display text-2xl">
                  <dt>Total</dt>
                  <dd data-testid="order-total">{fmt(total)}</dd>
                </div>
              </dl>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}
